import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Send, CheckCircle2, XCircle, BookCheck, RotateCcw, Ban, Plus, FileText, History, Paperclip, ShieldCheck, Search, X, ExternalLink,
} from 'lucide-react';

import { MasterReportPreview } from '@/components/master/report/MasterReportPreview';
import { ApBillVoucher }        from '@/components/ap/ApBillVoucher';
import { exportToExcel }        from '@/utils/reportExport';
import { apBillApi }      from '@/api/apBill.api';
import { supplierApi }    from '@/api/supplier.api';
import { accountApi }     from '@/api/account.api';
import { taxApi }         from '@/api/tax.api';
import { currencyApi }    from '@/api/currency.api';
import { exchangeRateApi } from '@/api/exchangeRate.api';
import { usePermission }  from '@/hooks/usePermission';
import { Permissions }    from '@/constants/permissions';
import { AppRoutes }      from '@/constants/routes';
import { Spinner }        from '@/components/ui/Spinner';
import { Button }        from '@/components/ui/Button';
import { IconButton }    from '@/components/ui/IconButton';
import { Input }         from '@/components/ui/Input';
import { Select }        from '@/components/ui/Select';
import { Field }         from '@/components/ui/Field';
import { Tabs }          from '@/components/ui/Tabs';
import { ReasonDialog }  from '@/components/ui/ReasonDialog';
import { PageHeader }    from '@/components/layout/PageHeader';
import { MasterToolbar } from '@/components/master/MasterToolbar';
import { MasterFlash }   from '@/components/master/MasterFlash';
import { AccountLookupField } from '@/components/erp/AccountLookupField';
import { LinesGrid, type LineColumn } from '@/components/grids/LinesGrid';
import { MasterSearchModal } from '@/components/master/MasterSearchModal';
import { ERPContextBar }    from '@/components/erp/ERPContextBar';
import { WorkflowStatusPanel } from '@/components/erp/WorkflowStatusPanel';
import { AttachmentPanel }   from '@/components/erp/AttachmentPanel';
import { AuditViewer }       from '@/components/erp/AuditViewer';
import { setErpContext, clearErpContext } from '@/state/erpContextSlice';
import { cn }              from '@/utils/cn';

import type { RootState }       from '@/state/store';
import { costCentresForLocation } from '@/utils/costCentreScope';
import type { SearchColumn }    from '@/components/master/MasterSearchModal';
import type { TaxCode }        from '@/api/tax.api';
import type { SupplierListDto } from '@/types/supplier.types';
import type {
  ApBillDto,
  ApBillLineRequest,
  CreateApBillRequest,
  UpdateApBillRequest,
  ApWorkflowStatus,
} from '@/types/apBill.types';
import {
  AP_WORKFLOW_STATUS_LABELS,
  AP_WORKFLOW_STATUS_COLORS,
} from '@/types/apBill.types';

// ── Column defs for search modals ─────────────────────────────────────────────

const SUPPLIER_COLUMNS: SearchColumn<SupplierListDto>[] = [
  { key: 'code', label: 'Code', className: 'w-28 font-mono text-orange-700 dark:text-orange-400'},
  { key: 'name', label: 'Supplier Name' },
  { key: 'currencyCode', label: 'CCY', className: 'w-16 font-mono text-slate-500 dark:text-gray-400' },
];

// ── Line row ──────────────────────────────────────────────────────────────────

interface LineRow {
  key:                    string;
  lineNumber:             number;
  accountId:              string;
  accountCode:            string;
  accountName:            string;
  inputTaxAccountId:      string;
  inputTaxAccountCode:    string;
  inputTaxAccountName:    string;
  costCenterId:           string;
  locationId:             string;
  description:            string;
  quantity:               number;
  unitPrice:              number;
  discountPercent:        number;
  taxRate:                number;
  remarks:                string;
  // computed (display-only)
  discountAmount:         number;
  lineAmount:             number;
  taxAmount:              number;
  totalAmount:            number;
}

const computeLine = (l: Omit<LineRow, 'discountAmount' | 'lineAmount' | 'taxAmount' | 'totalAmount'>): LineRow => {
  const disc = Math.round(l.quantity * l.unitPrice * (l.discountPercent / 100) * 10000) / 10000;
  const line = Math.round((l.quantity * l.unitPrice - disc) * 10000) / 10000;
  const tax  = Math.round(line * (l.taxRate / 100) * 10000) / 10000;
  return { ...l, discountAmount: disc, lineAmount: line, taxAmount: tax, totalAmount: line + tax };
};

const mkLine = (n: number): LineRow => computeLine({
  key:                    `new-${Date.now()}-${n}-${Math.random()}`,
  lineNumber:             n,
  accountId:              '',
  accountCode:            '',
  accountName:            '',
  inputTaxAccountId:      '',
  inputTaxAccountCode:    '',
  inputTaxAccountName:    '',
  costCenterId:           '',
  locationId:             '',
  description:            '',
  quantity:               1,
  unitPrice:              0,
  discountPercent:        0,
  taxRate:                0,
  remarks:                '',
});

// ── Inline account cell ───────────────────────────────────────────────────────

// ── Tabs ──────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'lines',       label: 'Bill Lines',   icon: FileText    },
  { key: 'workflow',    label: 'Workflow',      icon: History     },
  { key: 'attachments', label: 'Attachments',   icon: Paperclip   },
  { key: 'audit',       label: 'Audit Log',     icon: ShieldCheck },
] as const;
type Tab = (typeof TABS)[number]['key'];

const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Main Component ────────────────────────────────────────────────────────────

export const ApBillFormPage = () => {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isNew    = !id;

  const { hasPermission } = usePermission();
  const canEdit    = hasPermission(Permissions.AP_BILL_EDIT);
  const canSubmit  = hasPermission(Permissions.AP_BILL_SUBMIT);
  const canApprove = hasPermission(Permissions.AP_BILL_APPROVE);
  const canReject  = hasPermission(Permissions.AP_BILL_REJECT);
  const canPost    = hasPermission(Permissions.AP_BILL_POST);
  const canReverse = hasPermission(Permissions.AP_BILL_REVERSE);
  const canCancel  = hasPermission(Permissions.AP_BILL_CANCEL);
  const canDelete  = hasPermission(Permissions.AP_BILL_DELETE);
  const canAddDoc  = hasPermission(Permissions.DOC_ATTACHMENT_ADD);
  const canDelDoc  = hasPermission(Permissions.DOC_ATTACHMENT_DELETE);

  const authUser   = useSelector((s: RootState) => s.auth.user);
  const session    = useSelector((s: RootState) => s.session.session);
  const accessibleLocations   = useSelector((s: RootState) => s.auth.accessibleLocations);
  const accessibleCostCenters = useSelector((s: RootState) => s.auth.accessibleCostCenters);

  // ── Reference data ────────────────────────────────────────────────────────
  const [currencies, setCurrencies] = useState<{ code: string; name: string }[]>([]);

  useEffect(() => {
    currencyApi.getList({ pageSize: 100, isActive: true })
      .then(r => setCurrencies((r.data.data?.items ?? []).map((c: { code: string; name: string }) => ({ code: c.code, name: c.name }))))
      .catch(() => {});
  }, []);

  // ── Header state ──────────────────────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const baseCurrency = authUser?.baseCurrencyCode ?? session?.companyCurrency ?? 'USD';

  const [supplierId,              setSupplierId]              = useState('');
  const [supplierCode,            setSupplierCode]            = useState('');
  const [supplierName,            setSupplierName]            = useState('');
  const [controlAccountId,        setControlAccountId]        = useState<string | null>(null);
  const [supplierInvoiceNumber,   setSupplierInvoiceNumber]   = useState('');
  const [billDate,                setBillDate]                = useState(today);
  const [postingDate,             setPostingDate]             = useState(today);
  const [dueDate,                 setDueDate]                 = useState('');
  const [currencyCode,            setCurrencyCode]            = useState(baseCurrency);
  const [exchangeRate,            setExchangeRate]            = useState(1);
  const [reference,               setReference]               = useState('');
  const [narration,               setNarration]               = useState('');
  const [locationId,              setLocationId]              = useState('');
  const [costCenterId,            setCostCenterId]            = useState('');

  // Supplier search modal
  const [supplierOpen, setSupplierOpen] = useState(false);

  const fetchSuppliers = async (search: string, page: number, pageSize: number) => {
    const res = await supplierApi.getList({ search, pageNumber: page, pageSize, isActive: true });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  };

  // Seed location/CC from session on new
  useEffect(() => {
    if (!isNew) return;
    setLocationId(session?.locationId ?? '');
    setCostCenterId(session?.costCenterId ?? '');
  }, [isNew, session?.locationId, session?.costCenterId]);

  // Auto-fetch exchange rate when currency or date changes
  useEffect(() => {
    if (!currencyCode || currencyCode === baseCurrency) {
      if (isNew) setExchangeRate(1);
      return;
    }
    exchangeRateApi.getList({ fromCurrencyCode: baseCurrency, toCurrencyCode: currencyCode, pageSize: 10 })
      .then(res => {
        const items = res.data?.data?.items ?? [];
        const sorted = items.filter((r: { effectiveDate: string }) => r.effectiveDate <= billDate)
          .sort((a: { effectiveDate: string }, b: { effectiveDate: string }) => b.effectiveDate.localeCompare(a.effectiveDate));
        if (sorted.length > 0) setExchangeRate(sorted[0].rate);
        else if (items.length > 0) setExchangeRate(items[0].rate);
      }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyCode, billDate]);

  // ── Lines ─────────────────────────────────────────────────────────────────
  const [lines, setLines] = useState<LineRow[]>([mkLine(1)]);

  // A tax code carries both the rate and the account the tax belongs in. A bare percentage says
  // nothing about the account, so the input tax was debited to the expense line instead of the
  // recoverable-tax account, hiding it from the tax return.
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);
  const [accountByCode, setAccountByCode] = useState<Record<string, { id: string; name: string }>>({});

  useEffect(() => {
    taxApi.getCodes({ isActive: true, pageSize: 200 })
      .then(r => setTaxCodes(r.data?.data?.items ?? []))
      .catch(() => setTaxCodes([]));

    accountApi.getList({ pageNumber: 1, pageSize: 500 })
      .then(r => {
        const map: Record<string, { id: string; name: string }> = {};
        for (const a of r.data?.data?.items ?? []) map[a.code] = { id: a.id, name: a.name };
        setAccountByCode(map);
      })
      .catch(() => setAccountByCode({}));
  }, []);

  const CUSTOM_TAX = '__custom';

  // Derived, not stored: the codes load after a saved bill's lines do. Matched on the rate and,
  // when the line has one, the input account, which together identify the code.
  const taxCodeFor = (l: LineRow): TaxCode | undefined =>
    taxCodes.find(t =>
      t.taxRate === l.taxRate
      && (!l.inputTaxAccountId
          || accountByCode[t.glMapping?.inputTaxAccountCode ?? '']?.id === l.inputTaxAccountId));

  const pickTaxCode = (key: string, codeId: string) => {
    const code = taxCodes.find(t => t.id === codeId);
    const acc  = code ? accountByCode[code.glMapping?.inputTaxAccountCode ?? ''] : undefined;
    updateLine(key, {
      taxRate:             code?.taxRate ?? 0,
      inputTaxAccountId:   acc?.id ?? '',
      inputTaxAccountCode: code?.glMapping?.inputTaxAccountCode ?? '',
      inputTaxAccountName: acc?.name ?? '',
    });
  };

  const updateLine = (key: string, patch: Partial<LineRow>) =>
    setLines(ls => ls.map(l => l.key === key ? computeLine({ ...l, ...patch }) : l));

  const addLine = () =>
    setLines(ls => [...ls, mkLine(ls.length + 1)]);

  const removeLine = (key: string) =>
    setLines(ls => ls.filter(l => l.key !== key));

  // totals
  const subTotal   = lines.reduce((s, l) => s + l.lineAmount, 0);
  const taxTotal   = lines.reduce((s, l) => s + l.taxAmount,  0);
  const grandTotal = lines.reduce((s, l) => s + l.totalAmount, 0);

  // ── Bill state ────────────────────────────────────────────────────────────
  const [bill,     setBill]    = useState<ApBillDto | null>(null);
  const [loading,  setLoading] = useState(!isNew);
  const [saving,   setSaving]  = useState(false);
  const [error,    setError]   = useState<string | null>(null);
  const [success,  setSuccess] = useState<string | null>(null);
  const [tab,      setTab]     = useState<Tab>('lines');
  const [modal,    setModal]   = useState<'approve' | 'reject' | 'reverse' | 'cancel' | 'delete' | null>(null);
  const [printOpen, setPrintOpen] = useState(false);

  // ── Load bill ─────────────────────────────────────────────────────────────
  const loadBill = useCallback(async () => {
    if (isNew || !id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apBillApi.getById(id);
      const b = res.data?.data;
      if (!b) throw new Error('Bill not found');
      setBill(b);

      // populate header
      setSupplierId(b.supplierId);
      setSupplierCode(b.supplierCode);
      setSupplierName(b.supplierName);
      setControlAccountId(b.controlAccountId ?? null);
      setSupplierInvoiceNumber(b.supplierInvoiceNumber ?? '');
      setBillDate(b.billDate.split('T')[0]);
      setPostingDate(b.postingDate.split('T')[0]);
      setDueDate(b.dueDate ? b.dueDate.split('T')[0] : '');
      setCurrencyCode(b.currencyCode);
      setExchangeRate(b.exchangeRate);
      setReference(b.reference ?? '');
      setNarration(b.narration ?? '');
      setLocationId(b.locationId ?? '');
      setCostCenterId(b.costCenterId ?? '');

      // populate lines
      setLines(b.lines.map(l => computeLine({
        key:                    l.id,
        lineNumber:             l.lineNumber,
        accountId:              l.accountId,
        accountCode:            l.accountCode ?? '',
        accountName:            l.accountName ?? '',
        inputTaxAccountId:      l.inputTaxAccountId ?? '',
        inputTaxAccountCode:    '',
        inputTaxAccountName:    '',
        costCenterId:           l.costCenterId ?? '',
        locationId:             l.locationId ?? '',
        description:            l.description ?? '',
        quantity:               l.quantity,
        unitPrice:              l.unitPrice,
        discountPercent:        l.discountPercent,
        taxRate:                l.taxRate,
        remarks:                l.remarks ?? '',
      })));

      dispatch(setErpContext({
        companyCode:         authUser?.companyId?.substring(0, 8).toUpperCase() ?? 'COMPANY',
        companyName:         '',
        locationCode:        null,
        locationName:        null,
        costCenterCode:      null,
        costCenterName:      null,
        financialYearCode:   b.financialYearCode,
        financialPeriodCode: b.financialPeriodCode,
        transactionTypeCode: 'APBILL',
        documentNumber:      b.billNumber,
        currencyCode:        b.currencyCode,
        exchangeRate:        b.exchangeRate,
      }));
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to load bill');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, dispatch, authUser]);

  useEffect(() => { loadBill(); }, [loadBill]);
  useEffect(() => { return () => { dispatch(clearErpContext()); }; }, [dispatch]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const status: ApWorkflowStatus = bill?.workflowStatus ?? 'Draft';
  const isDraftEditable = isNew || status === 'Draft' || status === 'Rejected';
  const isEditable      = isDraftEditable && (isNew ? hasPermission(Permissions.AP_BILL_CREATE) : canEdit);

  // ── Build payload ─────────────────────────────────────────────────────────
  const buildLines = (): ApBillLineRequest[] =>
    lines.filter(l => l.accountId || l.unitPrice).map((l, idx) => ({
      lineNumber:         idx + 1,
      description:        l.description || null,
      accountId:          l.accountId,
      inputTaxAccountId:  l.inputTaxAccountId || null,
      costCenterId:       l.costCenterId || null,
      locationId:         l.locationId || null,
      quantity:           l.quantity,
      unitPrice:          l.unitPrice,
      discountPercent:    l.discountPercent,
      taxRate:            l.taxRate,
      remarks:            l.remarks || null,
    }));

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError(null);
    if (!supplierId) { setError('Please select a supplier.'); return; }
    const linesPayload = buildLines();
    if (linesPayload.length === 0) { setError('At least one bill line is required.'); return; }
    if (linesPayload.some(l => !l.accountId)) { setError('All lines must have an account selected.'); return; }
    if (grandTotal <= 0) { setError('Bill total must be greater than zero.'); return; }

    setSaving(true);
    try {
      if (isNew) {
        const payload: CreateApBillRequest = {
          locationId:           locationId || null,
          costCenterId:         costCenterId || null,
          financialYearCode:    session?.financialYearCode ?? '',
          financialPeriodCode:  session?.financialPeriodCode ?? '',
          supplierId,
          supplierInvoiceNumber: supplierInvoiceNumber || null,
          billDate, postingDate,
          dueDate: dueDate || null,
          currencyCode, exchangeRate,
          reference: reference || null,
          narration: narration || null,
          lines: linesPayload,
        };
        const res = await apBillApi.create(payload);
        const newId = res.data?.data?.id;
        setSuccess('Bill created successfully.');
        setTimeout(() => navigate(`/ap/bills/${newId}`), 800);
      } else if (bill) {
        const payload: UpdateApBillRequest = {
          locationId:           locationId || null,
          costCenterId:         costCenterId || null,
          supplierId, supplierCode, supplierName,
          controlAccountId:     controlAccountId ?? null,
          supplierInvoiceNumber: supplierInvoiceNumber || null,
          billDate, postingDate,
          dueDate: dueDate || null,
          currencyCode, exchangeRate,
          reference: reference || null,
          narration: narration || null,
          lines: linesPayload,
        };
        await apBillApi.update(bill.id, payload);
        setSuccess('Bill updated.');
        setTimeout(() => { setSuccess(null); loadBill(); }, 800);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to save bill.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Workflow actions ──────────────────────────────────────────────────────
  const doAction = async (action: () => Promise<void>, successMsg: string) => {
    setError(null);
    setSaving(true);
    try {
      await action();
      setSuccess(successMsg);
      setTimeout(() => { setSuccess(null); loadBill(); }, 1200);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Action failed.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit  = () => doAction(async () => { await apBillApi.submit(bill!.id); }, 'Bill submitted for approval.');
  const handlePost    = () => doAction(async () => { await apBillApi.post(bill!.id); }, 'Bill posted to general ledger.');
  const handleApprove = (remarks: string) => doAction(async () => { await apBillApi.approve(bill!.id, remarks); }, 'Bill approved.');
  const handleReject  = (reason: string)  => doAction(async () => { await apBillApi.reject(bill!.id, reason); }, 'Bill rejected.');
  const handleCancel  = (reason: string)  => doAction(async () => { await apBillApi.cancel(bill!.id, reason); }, 'Bill cancelled.');
  const handleDelete  = async () => {
    if (!bill) return;
    setSaving(true); setError(null);
    try {
      await apBillApi.delete(bill.id);
      navigate(AppRoutes.AP_BILLS, { replace: true });
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Delete failed.');
    } finally { setSaving(false); }
  };
  const handleReverse = (narr: string) => doAction(async () => {
    const today2 = new Date().toISOString().split('T')[0];
    const res = await apBillApi.reverse(bill!.id, today2, today2, narr || undefined);
    const revId = res.data?.data?.id;
    setTimeout(() => navigate(`/ap/bills/${revId}`), 1200);
  }, 'Reversal bill created.');

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return <div className="flex items-center justify-center h-full"><Spinner size="lg" /></div>;
  }

  // ── Render ────────────────────────────────────────────────────────────────
  // Cost centres a line may name — the same list the header offers (422467).
  const lineCostCentres = costCentresForLocation(accessibleCostCenters, locationId)
    .map(cc => ({ value: cc.id, label: `${cc.code} — ${cc.name}` }));

  const lineColumns: LineColumn<LineRow>[] = [
    {
      key: 'expense-account', header: 'Expense Account', width: 'min-w-[200px]',
      render: (line) => (
        <>
          <AccountLookupField
            value={line.accountId || null}
            displayCode={line.accountCode}
            displayName={line.accountName}
            isPostingAccount
            disabled={!isEditable}
            placeholder="Select account…"
            onChange={(id, code, name) => updateLine(line.key, { accountId: id ?? '', accountCode: code ?? '', accountName: name ?? '' })}
          />
        </>
      ),
    },
    {
      key: 'input-tax-acct', header: 'Input Tax Acct', width: 'min-w-[180px]',
      render: (line) => (
        <>
          <AccountLookupField
            value={line.inputTaxAccountId || null}
            displayCode={line.inputTaxAccountCode}
            displayName={line.inputTaxAccountName}
            isPostingAccount
            disabled={!isEditable}
            placeholder="Select account…"
            onChange={(id, code, name) => updateLine(line.key, { inputTaxAccountId: id ?? '', inputTaxAccountCode: code ?? '', inputTaxAccountName: name ?? '' })}
          />
        </>
      ),
    },
    {
      // Line-level cost centre (422467): blank = the header's. A line may differ from the header,
      // e.g. one invoice split across departments; the server re-checks every id on save.
      key: 'cost-centre', header: 'Cost Centre', width: 'min-w-[170px]',
      render: (line, idx) => (
        <>
          <Select size="xs" aria-label={`Cost centre line ${idx + 1}`}
            value={line.costCenterId}
            onChange={e => updateLine(line.key, { costCenterId: e.target.value })}
            disabled={!isEditable}
            placeholder="— Header —"
            options={line.costCenterId && !lineCostCentres.some(o => o.value === line.costCenterId)
              // Saved earlier but not offered here (another location, or outside this user's access):
              // keep it visible rather than showing the line as if it had none.
              ? [...lineCostCentres, { value: line.costCenterId, label: '(not available here)' }]
              : lineCostCentres}
          />
        </>
      ),
    },
    {
      key: 'description', header: 'Description', width: 'min-w-[160px]',
      render: (line) => (
        <>
          <Input size="xs" value={line.description}
            onChange={e => updateLine(line.key, { description: e.target.value })}
            disabled={!isEditable} placeholder="Item description…"
          />
        </>
      ),
    },
    {
      key: 'qty', header: 'Qty', align: 'right', width: 'w-20',
      render: (line) => (
        <>
          <Input size="xs" type="number" value={line.quantity || ''}
            onChange={e => updateLine(line.key, { quantity: parseFloat(e.target.value) || 0 })}
            disabled={!isEditable} min="0" step="1" className="text-right font-mono"
          />
        </>
      ),
    },
    {
      key: 'unit-price', header: 'Unit Price', align: 'right', width: 'w-28',
      render: (line) => (
        <>
          <Input size="xs" type="number" value={line.unitPrice || ''}
            onChange={e => updateLine(line.key, { unitPrice: parseFloat(e.target.value) || 0 })}
            disabled={!isEditable} min="0" step="0.01" className="text-right font-mono"
          />
        </>
      ),
    },
    {
      key: 'disc', header: 'Disc %', align: 'right', width: 'w-20',
      render: (line) => (
        <>
          <Input size="xs" type="number" value={line.discountPercent || ''}
            onChange={e => updateLine(line.key, { discountPercent: parseFloat(e.target.value) || 0 })}
            disabled={!isEditable} min="0" max="100" step="0.01" className="text-right font-mono"
          />
        </>
      ),
    },
    {
      key: 'net-amt', header: 'Net Amt', align: 'right', width: 'w-28', className: 'text-sm font-mono text-slate-700 dark:text-slate-300',
      render: (line) => (
        <>
          {fmt(line.lineAmount)}
        </>
      ),
    },
    {
      key: 'tax', header: 'Tax', align: 'right', width: 'w-28',
      render: (line, idx) => (
        <>
          <Select size="xs"
            aria-label={`Tax line ${idx + 1}`}
            value={taxCodeFor(line)?.id ?? (line.taxRate > 0 ? CUSTOM_TAX : '')}
            onChange={e => { if (e.target.value !== CUSTOM_TAX) pickTaxCode(line.key, e.target.value); }}
            disabled={!isEditable}
          >
            <option value="">No tax</option>
            {line.taxRate > 0 && !taxCodeFor(line) && (
              <option value={CUSTOM_TAX}>{line.taxRate}% (custom)</option>
            )}
            {taxCodes.map(t => (
              <option key={t.id} value={t.id}>{t.code} ({t.taxRate}%)</option>
            ))}
          </Select>
        </>
      ),
    },
    {
      key: 'total', header: 'Total', align: 'right', width: 'w-28', className: 'text-sm font-mono font-semibold text-slate-900 dark:text-white',
      render: (line) => (
        <>
          {fmt(line.totalAmount)}
        </>
      ),
    },
  ];

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-hidden">

      {/* ERP Context Bar */}
      <ERPContextBar />

      <MasterToolbar
        onRefresh={() => void loadBill()}
        refreshEnabled={!isNew}
        onSave={() => void handleSave()}
        saveEnabled={isEditable}
        onEdit={() => {}}
        onPrint={() => setPrintOpen(true)}
        printEnabled={!isNew && !!bill}
        onDelete={() => setModal('delete')}
        deleteEnabled={!isNew && status === 'Draft' && canDelete}
        onExit={() => navigate(AppRoutes.AP_BILLS)}
        saving={saving}
        actions={[
          ...(!isNew && status === 'Draft' && canSubmit
            ? [{ key: 'submit', label: 'Submit', icon: <Send size={15} />, color: 'sky' as const, onClick: () => void handleSubmit() }] : []),
          ...(!isNew && (status === 'Submitted' || status === 'PendingApproval') && canApprove
            ? [{ key: 'approve', label: 'Approve', icon: <CheckCircle2 size={15} />, color: 'emerald' as const, onClick: () => setModal('approve') }] : []),
          ...(!isNew && (status === 'Submitted' || status === 'PendingApproval') && canReject
            ? [{ key: 'reject', label: 'Reject', icon: <XCircle size={15} />, color: 'rose' as const, onClick: () => setModal('reject') }] : []),
          ...(!isNew && status === 'Approved' && canPost
            ? [{ key: 'post', label: 'Post', icon: <BookCheck size={15} />, color: 'teal' as const, onClick: () => void handlePost() }] : []),
          ...(!isNew && status === 'Posted' && canReverse && !bill?.reversalBillId
            ? [{ key: 'reverse', label: 'Reverse', icon: <RotateCcw size={15} />, color: 'amber' as const, onClick: () => setModal('reverse') }] : []),
          ...(!isNew && !['Draft', 'Posted', 'Cancelled', 'Reversed'].includes(status) && canCancel
            ? [{ key: 'cancel', label: 'Cancel', icon: <Ban size={15} />, color: 'rose' as const, onClick: () => setModal('cancel') }] : []),
        ]}
      />

      <MasterFlash message={error ? { type: 'error', text: error } : success ? { type: 'success', text: success } : null} />

      <PageHeader
        icon={FileText}
        title={isNew ? 'New AP Bill' : `Bill ${bill?.billNumber ?? ''}`}
        subtitle={!isNew && bill
          ? `Created ${new Date(bill.createdDate).toLocaleString()}${bill.modifiedDate ? ` · Modified ${new Date(bill.modifiedDate).toLocaleString()}` : ''}`
          : undefined}
        actions={!isNew ? (
          <div className="flex items-center gap-2">
            {bill?.originalBillId && (
              <Button variant="ghost" size="xs" rightIcon={<ExternalLink size={10} />}
                onClick={() => navigate(`/ap/bills/${bill.originalBillId}`)}>View Original</Button>
            )}
            {bill?.reversalBillId && (
              <Button variant="ghost" size="xs" rightIcon={<ExternalLink size={10} />}
                onClick={() => navigate(`/ap/bills/${bill.reversalBillId}`)}>View Reversal</Button>
            )}
            {bill?.sourceGlJournalId && (
              <Button variant="ghost" size="xs" rightIcon={<ExternalLink size={10} />}
                onClick={() => navigate(`/gl/journals/${bill.sourceGlJournalId}`)}>View GL Journal</Button>
            )}
            {bill?.isReversal && (
              <span className="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-xs font-semibold rounded-full">
                Reversal
              </span>
            )}
            <span className={cn('px-3 py-1 rounded-full text-xs font-semibold', AP_WORKFLOW_STATUS_COLORS[status])}>
              {AP_WORKFLOW_STATUS_LABELS[status]}
            </span>
          </div>
        ) : undefined}
      />

      {/* Header fields */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {/* Supplier — found through the search modal (server-side search), shown as code — name */}
          <Field label="Supplier *" className="col-span-2">
            <div className="flex items-center gap-1.5">
              <Input
                readOnly
                aria-label="Supplier"
                value={supplierId ? `${supplierCode} — ${supplierName}` : ''}
                placeholder={isEditable ? 'Select supplier…' : '—'}
                onClick={() => isEditable && setSupplierOpen(true)}
                fieldClassName="flex-1"
                className={cn(isEditable && 'cursor-pointer')}
              />
              {isEditable && (
                <IconButton label="Search suppliers" icon={<Search size={14} />} onClick={() => setSupplierOpen(true)} />
              )}
              {isEditable && supplierId && (
                <IconButton label="Clear supplier" icon={<X size={14} />}
                  onClick={() => { setSupplierId(''); setSupplierCode(''); setSupplierName(''); setControlAccountId(null); }} />
              )}
            </div>
          </Field>

          <Input label="Supplier Inv #" value={supplierInvoiceNumber} onChange={e => setSupplierInvoiceNumber(e.target.value)}
            disabled={!isEditable} placeholder="Supplier's own invoice reference" />

          <Input label="Bill Date *" type="date" value={billDate}
            onChange={e => setBillDate(e.target.value)} disabled={!isEditable} />

          <Input label="Posting Date *" type="date" value={postingDate}
            onChange={e => setPostingDate(e.target.value)} disabled={!isEditable} />

          <Input label="Due Date" type="date" value={dueDate}
            onChange={e => setDueDate(e.target.value)} disabled={!isEditable} />

          {currencies.length > 0 ? (
            <Select label="Currency *" value={currencyCode} onChange={e => setCurrencyCode(e.target.value)}
              disabled={!isEditable} className="font-mono"
              options={currencies.map(c => ({ value: c.code, label: `${c.code} — ${c.name}` }))} />
          ) : (
            <Input label="Currency *" value={currencyCode} onChange={e => setCurrencyCode(e.target.value.toUpperCase())}
              disabled={!isEditable} maxLength={10} className="font-mono uppercase" />
          )}

          <Input label="Exchange Rate" type="number" value={exchangeRate}
            onChange={e => setExchangeRate(parseFloat(e.target.value) || 1)}
            disabled={!isEditable} step="0.000001" min="0.000001" />

          <Input label="Reference" value={reference} onChange={e => setReference(e.target.value)}
            disabled={!isEditable} placeholder="Optional" />

          <Select label="Location" value={locationId} onChange={e => setLocationId(e.target.value)}
            disabled={!isEditable} fieldClassName="col-span-2 md:col-span-3"
            placeholder="— All Locations —"
            options={accessibleLocations.map(l => ({ value: l.id, label: `${l.code} — ${l.name}` }))} />

          <Select label="Cost Center" value={costCenterId} onChange={e => setCostCenterId(e.target.value)}
            disabled={!isEditable} fieldClassName="col-span-2 md:col-span-3"
            placeholder="— None —"
            options={costCentresForLocation(accessibleCostCenters, locationId).map(cc => ({ value: cc.id, label: `${cc.code} — ${cc.name}` }))} />

          <Input label="Narration" value={narration} onChange={e => setNarration(e.target.value)}
            disabled={!isEditable} placeholder="Describe this bill…"
            fieldClassName="col-span-2 md:col-span-3 lg:col-span-6" />
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        tabs={TABS.map(t => t.label)}
        active={TABS.find(t => t.key === tab)?.label ?? TABS[0].label}
        onChange={label => setTab(TABS.find(t => t.label === label)?.key ?? 'lines')}
        className="static"
      />

      {/* Tab body */}
      <div className="flex-1 overflow-auto">

        {/* ── BILL LINES ────────────────────────────────────────────────────── */}
        {tab === 'lines' && (
          <LinesGrid
            rows={lines}
            columns={lineColumns}
            rowKey={line => line.key}
            onRemove={isEditable ? line => removeLine(line.key) : undefined}
            footer={
              <>
              {isEditable && (
                <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-700">
                  <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={addLine}>
                    Add Line
                  </Button>
                </div>
              )}

              <div className="flex items-end justify-end gap-8 px-6 py-3 bg-slate-50 dark:bg-slate-800/80">
                <div className="text-right">
                  <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">Sub Total</p>
                  <p className="text-sm font-semibold font-mono text-slate-700 dark:text-slate-300">{fmt(subTotal)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">Tax</p>
                  <p className="text-sm font-semibold font-mono text-slate-700 dark:text-slate-300">{fmt(taxTotal)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">Total ({currencyCode})</p>
                  <p className="text-base font-bold font-mono text-brand-700 dark:text-brand-400">{fmt(grandTotal)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">Base Amount</p>
                  <p className="text-sm font-semibold font-mono text-slate-700 dark:text-slate-300">
                    {fmt(grandTotal * exchangeRate)}
                  </p>
                </div>
              </div>
              </>
            }
          />
        )}

        {/* ── WORKFLOW ──────────────────────────────────────────────────────── */}
        {tab === 'workflow' && (
          <div className="p-6 space-y-6">
            {!isNew && bill && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">Approval Status</h3>
                <WorkflowStatusPanel entityName="ApBill" entityId={bill.id} />
              </div>
            )}
            {isNew && (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <History size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the bill first to track workflow.</p>
              </div>
            )}
          </div>
        )}

        {/* ── ATTACHMENTS ──────────────────────────────────────────────────── */}
        {tab === 'attachments' && (
          <div className="p-6">
            {!isNew && bill ? (
              <AttachmentPanel entityName="ApBill" entityId={bill.id} canAdd={canAddDoc} canDelete={canDelDoc} />
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <Paperclip size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the bill first to add attachments.</p>
              </div>
            )}
          </div>
        )}

        {/* ── AUDIT LOG ─────────────────────────────────────────────────────── */}
        {tab === 'audit' && (
          <div className="p-6">
            {!isNew && bill ? (
              <AuditViewer entityName="ApBill" entityId={bill.id} />
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <ShieldCheck size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the bill first to view audit history.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Modals */}
      {modal === 'approve' && (
        <ReasonDialog title="Approve Bill" placeholder="Approval remarks (optional)…" required={false}
          onConfirm={text => { setModal(null); handleApprove(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'reject' && (
        <ReasonDialog title="Reject Bill" placeholder="Rejection reason (required)…" required={true}
          onConfirm={text => { setModal(null); handleReject(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'reverse' && (
        <ReasonDialog title="Create Reversal Bill" placeholder="Reversal bill narration (optional)…" required={false}
          onConfirm={text => { setModal(null); handleReverse(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'cancel' && (
        <ReasonDialog title="Cancel Bill" placeholder="Cancellation reason (required)…" required={true}
          onConfirm={text => { setModal(null); handleCancel(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'delete' && (
        <ReasonDialog title="Delete Draft Bill"
          placeholder="This draft bill will be permanently removed. Type a note or leave blank."
          required={false}
          onConfirm={() => { setModal(null); handleDelete(); }}
          onCancel={() => setModal(null)} />
      )}

      {/* Supplier search modal */}
      <MasterSearchModal<SupplierListDto>
        isOpen={supplierOpen}
        onClose={() => setSupplierOpen(false)}
        title="Select Supplier"
        columns={SUPPLIER_COLUMNS}
        fetchData={fetchSuppliers}
        onSelect={item => {
          setSupplierId(item.id);
          setSupplierCode(item.code);
          setSupplierName(item.name);
          if ((item as SupplierListDto & { controlAccountId?: string }).controlAccountId) {
            setControlAccountId((item as SupplierListDto & { controlAccountId?: string }).controlAccountId!);
          }
          if (item.currencyCode) setCurrencyCode(item.currencyCode);
          setSupplierOpen(false);
        }}
      />

      {/* Print Preview */}
      {bill && (
        <MasterReportPreview
          isOpen={printOpen}
          onClose={() => setPrintOpen(false)}
          title={`Bill ${bill.billNumber}`}
          fileBaseName={`AP-Bill-${bill.billNumber}`}
          onDownloadExcel={() => exportToExcel({
            sheetName: 'Bill',
            title:    `AP Bill — ${bill.billNumber}`,
            subtitle: `Supplier: ${bill.supplierName ?? ''}   Date: ${bill.billDate?.slice(0, 10) ?? ''}   Currency: ${bill.currencyCode ?? ''}`,
            columns: [
              { header: '#',            key: 'lineNumber',  width: 6,  align: 'center' },
              { header: 'Account Code', key: 'accountCode', width: 16 },
              { header: 'Account Name', key: 'accountName', width: 28 },
              { header: 'Description',  key: 'description',  width: 34 },
              { header: 'Qty',          key: 'quantity',    width: 10, numFmt: '#,##0.00', align: 'right' },
              { header: 'Unit Price',   key: 'unitPrice',   width: 14, numFmt: '#,##0.00', align: 'right' },
              { header: 'Net Amount',   key: 'lineAmount',  width: 14, numFmt: '#,##0.00', align: 'right' },
              { header: 'Tax Amount',   key: 'taxAmount',   width: 14, numFmt: '#,##0.00', align: 'right' },
              { header: 'Total',        key: 'totalAmount', width: 16, numFmt: '#,##0.00', align: 'right' },
            ],
            rows: (bill.lines ?? []).map(l => ({
              lineNumber:  l.lineNumber,
              accountCode: l.accountCode ?? '',
              accountName: l.accountName ?? '',
              description: l.description ?? '',
              quantity:    l.quantity   || '',
              unitPrice:   l.unitPrice  || '',
              lineAmount:  l.lineAmount || '',
              taxAmount:   l.taxAmount  || '',
              totalAmount: l.totalAmount || '',
            })),
            totalsRow: { description: 'TOTAL', lineAmount: bill.subTotal ?? 0, taxAmount: bill.taxAmount ?? 0, totalAmount: bill.totalAmount ?? 0 },
          }, `AP-Bill-${bill.billNumber}`)}
          email={{ documentType: 'AP Bill', documentNo: bill.billNumber }}
        >
          <ApBillVoucher
            bill={bill}
            companyName={authUser?.companyId ? '' : 'myLedger ONE'}
            printedBy={authUser?.username}
          />
        </MasterReportPreview>
      )}
    </div>
  );
};
